/**
 * Shared components for Finance Views
 */
import React from 'react';
import LoadingComponent from '../../loadingComponent';

/**
 * Empty State Component
 * @param {string} message - Message to display
 */
export const EmptyState = ({ message = 'No data available' }) => {
    return (
        <div className="tab-content feetype-wise-content" style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#6B7280', fontFamily: 'InterRegular' }}>{message}</p>
        </div>
    );
};

/**
 * Loading Wrapper Component
 * @param {boolean} isLoading - Loading state
 * @param {React.ReactNode} children - Child components to render
 */
export const LoadingWrapper = ({ isLoading, children }) => {
    if (isLoading) {
        return <LoadingComponent />;
    }
    return <>{children}</>;
};

/**
 * Finance View Container Component
 * @param {string} title - View title
 * @param {React.ReactNode} children - Child components
 */
export const FinanceViewContainer = ({ title, children }) => {
    return (
        <div className="finance-view-container">
            <h2>{title}</h2>
            <div className="finance-content">
                {children}
            </div>
        </div>
    );
};

/**
 * Fee Filter Component
 * @param {string} activeFilter - Current active filter ('all', 'regular', 'misc')
 * @param {Function} onFilterChange - Callback when filter changes
 */
export const FeeFilter = ({ activeFilter, onFilterChange }) => {
    return (
        <div style={{
            display: 'flex',
            gap: '1.5rem',
            marginBottom: '1.5rem',
            alignItems: 'center',
            padding: '1rem',
            background: '#F9FAFB',
            borderRadius: '8px',
            border: '1px solid #E5E7EB'
        }}>
            <FilterRadioOption
                label="All Fee"
                checked={activeFilter === 'all'}
                onChange={() => onFilterChange('all')}
            />
            <FilterRadioOption
                label="Regular Fee"
                checked={activeFilter === 'regular'}
                onChange={() => onFilterChange('regular')}
            />
            <FilterRadioOption
                label="Misc Fee"
                checked={activeFilter === 'misc'}
                onChange={() => onFilterChange('misc')}
            />
        </div>
    );
};

/**
 * Filter Radio Option Component
 * @param {string} label - Label text
 * @param {boolean} checked - Whether checked
 * @param {Function} onChange - Callback when changed
 */
const FilterRadioOption = ({ label, checked, onChange }) => {
    return (
        <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            fontFamily: 'InterMedium',
            fontSize: '14px',
            color: '#1F2937'
        }}>
            <input
                type="radio"
                name="feeFilter"
                checked={checked}
                onChange={onChange}
                style={{ accentColor: '#076EFF', width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span>{label}</span>
        </label>
    );
};

/**
 * Amount Display Component
 * @param {number} amount - Amount to display
 * @param {boolean} isTotal - Whether this is a total row
 */
export const AmountDisplay = ({ amount, isTotal = false }) => {
    return (
        <span style={{
            fontFamily: isTotal ? 'InterSemiBold' : 'InterMedium',
            color: isTotal ? '#076EFF' : '#1F2937',
            fontSize: '0.9375rem',
            fontWeight: isTotal ? 600 : 'normal'
        }}>
            {amount.toFixed(2)}
        </span>
    );
};

/**
 * Status Badge Component
 * @param {string} status - Status text ('Approved', 'Pending', 'Rejected')
 */
export const StatusBadge = ({ status }) => {
    const statusStyles = {
        'Approved': {
            background: '#C8F4E3',
            color: '#065F46'
        },
        'Pending': {
            background: '#FEF3C7',
            color: '#92400E'
        },
        'Rejected': {
            background: '#FEE2E2',
            color: '#991B1B'
        }
    };

    const style = statusStyles[status] || {
        background: '#E5E7EB',
        color: '#000000'
    };

    return (
        <span style={{
            ...style,
            padding: '6px 16px',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontFamily: 'InterSemiBold',
            display: 'inline-block'
        }}>
            {status}
        </span>
    );
};

/**
 * Date Format Helper
 * @param {string} dateString - Date string to format
 * @returns {string} - Formatted date (DD/MM/YYYY)
 */
export const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};
